import { useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Teachr from "../../Objects/models/Teachr";
import SessionDescriptionRepository from "../../Repositories/SessionDescriptionRepository";
import BlockingPopup from "../../Components/BlockingPopup";
import { getActiveBrandConfig, getActivePrestationBrandConfig } from "../../config/environment.config";

/**
 * P4 – Vérification de la description générique avant déclaration.
 *
 * Même contrat que useTravelScenario : checkSessionDescription(onProceed)
 * appelle onProceed() si la description existe, sinon affiche un popup
 * bloquant dont le seul bouton renvoie vers Paramètres > Descriptions
 * (avec retour automatique vers la déclaration après enregistrement).
 * Une erreur réseau ne bloque jamais le parcours : le backend garde le
 * dernier mot (session_description_required).
 */
export const useSessionDescriptionCheck = (teachr: Teachr | undefined) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [popupOpen, setPopupOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    // mémoïse le résultat positif : inutile de re-vérifier à chaque étape
    const hasDescriptionRef = useRef(false);

    const checkSessionDescription = (onProceed: () => void) => {
        if (!teachr?.id || hasDescriptionRef.current) {
            onProceed();
            return;
        }
        setLoading(true);
        SessionDescriptionRepository.get(teachr.id)
            .then((response) => {
                if (response?.sessionDescription) {
                    hasDescriptionRef.current = true;
                    onProceed();
                } else {
                    setPopupOpen(true);
                }
            })
            .catch(() => {
                // jamais bloquant côté front en cas d'erreur réseau
                onProceed();
            })
            .finally(() => setLoading(false));
    };

    const handleGoToSettings = () => {
        setPopupOpen(false);
        navigate(`/${getActiveBrandConfig()}/tableau-de-bord/parametre`, {
            state: { step: 5, returnTo: location.pathname },
        });
    };

    const descriptionModal = (
        <BlockingPopup
            open={popupOpen}
            title="Description de vos séances requise"
            message={`Avant de déclarer, vous devez renseigner le déroulement habituel de vos ${getActivePrestationBrandConfig("s")}. Cette description figure sur les devis transmis à vos clients. Vous serez renvoyé vers votre déclaration une fois la description enregistrée.`}
            actionLabel="Renseigner ma description"
            onAction={handleGoToSettings}
        />
    );

    return { checkSessionDescription, descriptionModal, descriptionLoading: loading };
};

export default useSessionDescriptionCheck;
